import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { HsFilters, presetRange } from "@/components/hs-dashboard/HsFilters";
import { HsKpis } from "@/components/hs-dashboard/HsKpis";
import { HsCompliance, HsIncidentSummary } from "@/components/hs-dashboard/HsCompliance";
import { HsQuickActions, HsTraining, HsUpcoming } from "@/components/hs-dashboard/HsWorkPanels";
import { HsActions } from "@/components/hs-dashboard/HsActions";
import { card, muted } from "@/components/hs-dashboard/hsShared";

const HsActivityChart = lazy(() => import("@/components/hs-dashboard/HsActivityChart"));
const OFFICE = ["admin", "office", "super_admin"];
const Skeleton = ({ className }) => <div className={`${card} animate-pulse ${className}`} />;

export default function HealthSafetyOverview() {
  const { user } = useAuth();
  const [range, setRange] = useState(() => presetRange("30"));
  const [jobId, setJobId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const lastFocus = useRef(Date.now());

  useEffect(() => { api.get("/health-safety/dashboard/jobs").then(r => setJobs(r.data.items)).catch(() => setJobs([])); }, []);
  // Refresh on return to the tab at most once a minute; no polling.
  useEffect(() => {
    const onFocus = () => { if (Date.now() - lastFocus.current > 60000) { lastFocus.current = Date.now(); setRevision(n => n + 1); } };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    api.get("/health-safety/dashboard", { params: { from_date: range.from, to_date: range.to, job_id: jobId || undefined }, signal: controller.signal })
      .then(r => setData(r.data))
      .catch(e => { if (e.name !== "CanceledError") setError(e.response?.data?.detail || "Could not load Health & Safety data. Try again."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [range.from, range.to, jobId, revision]);
  const refresh = useCallback(() => setRevision(n => n + 1), []);

  return <div className="mx-auto max-w-[1700px] space-y-4 p-4 text-[#101820] dark:text-zinc-100 md:p-5" data-testid="health-safety-overview">
    <header className="flex flex-wrap items-center gap-x-4 gap-y-3 pb-1">
      <ShieldCheck className="h-10 w-10 shrink-0 text-[#FF5F15]" strokeWidth={1.8} aria-hidden />
      <div className="min-w-[220px] flex-1">
        <h1 className="text-2xl font-extrabold tracking-[-0.8px] sm:text-[27px]" data-testid="hs-title">Health &amp; Safety Overview</h1>
        <p className={`mt-0.5 text-sm ${muted}`}>Keep your people safe, meet your obligations and stay compliant.</p>
      </div>
      <HsFilters range={range} onRange={setRange} jobId={jobId} onJob={setJobId} jobs={jobs} />
    </header>
    {error && <p role="alert" data-testid="hs-error" className={`${card} p-3 text-sm text-red-700 dark:text-red-300`}>{error} <button className="font-bold underline" onClick={refresh}>Retry</button></p>}
    {!data ? !error && <div className="space-y-4" role="status" aria-label="Loading"><div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-[108px]" />)}</div><div className="grid gap-4 lg:grid-cols-3"><Skeleton className="h-64" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div></div> :
      <div className={`space-y-4 transition-opacity duration-200 ${loading ? "opacity-60" : ""}`} aria-busy={loading} data-testid="hs-dashboard-content">
        <HsKpis data={data} jobId={jobId} />
        <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-[1.38fr_1fr_0.86fr]">
          <Suspense fallback={<Skeleton className="h-64" />}><HsActivityChart activity={data.activity} bucket={data.range.bucket} /></Suspense>
          <HsCompliance c={data.compliance} />
          <div className="lg:col-span-2 xl:col-span-1"><HsIncidentSummary inc={data.incidents} jobId={jobId} /></div>
        </section>
        <section className="grid items-start gap-4 xl:grid-cols-[1.2fr_0.9fr]"><HsQuickActions /><HsUpcoming upcoming={data.upcoming} today={data.today} /></section>
        <section className="grid gap-4 xl:grid-cols-[1fr_0.95fr]"><HsActions actions={data.actions} jobId={jobId} canClose={OFFICE.includes(user?.role)} onChanged={refresh} /><HsTraining t={data.training} /></section>
        <p className={`text-[11px] ${muted}`} data-testid="hs-footnote">{data.range.from} to {data.range.to} · New Zealand time · Trends compare with the previous {data.range.bucket === "month" ? "period" : "equal-length period"}. Open actions, upcoming items and training show current status.</p>
      </div>}
  </div>;
}
